import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable, OnDestroy} from '@angular/core';
import {JwtHelperService} from '@auth0/angular-jwt';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {environment} from '../../environments/environment';
import {HasId, Timestamped} from '../shared/interfaces';

export interface ICredentials {
    email: string;
    password: string;
}

export interface IPasswordChangeFormData {
    password: string;
    password_confirmation: string;
}

export interface IUser extends HasId, Timestamped {
    email: string;
    firstname: string;
    lastname: string;
    nickname: string;
    roles?: string[];
}

export interface IUserToRegister extends IUser {
    password: string;
    password_confirmation: string;
}

@Injectable()
export class Auth implements OnDestroy {
    public static TOKEN_NAME = 'kkk-token';
    public static TOKEN_SET_TIME_KEY = 'token_set_time';
    public static JWT_CONFIG = {
        config: {
            tokenGetter: Auth.tokenGetter,
            whitelistedDomains: [environment.backendHost]
        }
    };

    public tokenChange: Subject<boolean>;

    private timeout;

    private static tokenGetter() {
        return Auth.token;
    }

    private static setToken(token: string): void {
        localStorage.setItem(Auth.TOKEN_NAME, token);
        localStorage.setItem(Auth.TOKEN_SET_TIME_KEY, new Date().valueOf() + '');
    }

    private static get token() {
        return localStorage.getItem(Auth.TOKEN_NAME);
    }

    private static get justRefreshed(): boolean {
        return new Date().valueOf() - (+localStorage.getItem(Auth.TOKEN_SET_TIME_KEY)) < 5000;
    }

    private onRefreshError: (e: any) => void = () => void 0;

    get user(): IUser {
        return this.isLoggedIn() && this.jwtHelper.decodeToken(Auth.token).user;
    }

    constructor(
        private http: HttpClient,
        private jwtHelper: JwtHelperService,
    ) {
        this.tokenChange = new Subject();
        const token = Auth.token;
        try {
            if (!token || this.jwtHelper.isTokenExpired(token)) {
                return;
            }
        } catch (e) {
            this.logOut();
            return;
        }
        this.startRenewing();
    }

    public ngOnDestroy(): void {
        this.clearTimeout();
    }

    public setOnRefreshError(fn: (e: any) => void) {
        this.onRefreshError = fn;
    }

    isLoggedIn() {
        return Auth.token && !this.jwtHelper.isTokenExpired();
    }

    public doLogin(credentials: ICredentials): Observable<{}> {
        return this.http.post(environment.api + '/auth/login', credentials)
                   .map(this.handleNewTokenResponse.bind(this));
    }

    public logOut(): void {
        Auth.setToken(void 0);
        localStorage.clear();
        sessionStorage.clear();
        this.emitTokenChange();
    }

    public refresh(): void {
        if (Auth.justRefreshed) {
            this.startRenewing();
            return;
        }
        this.http.get(environment.api + '/auth/refresh')
            .catch(e => {
                this.onRefreshError.apply(null, e);
                throw e;
            })
            .map(this.handleNewTokenResponse.bind(this))
            .subscribe();
    }

    public signUp(user: IUserToRegister): Observable<{}> {
        const headers: HttpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
        return this.http.post(environment.api + '/auth/signup', user, {headers: headers})
                   .map(this.handleNewTokenResponse.bind(this));
    }

    public changePassword(passwords: IPasswordChangeFormData): Observable<{}> {
        return this.http.put(environment.api + '/auth/change', passwords)
                   .map(this.handleNewTokenResponse.bind(this));
    }

    public changeProfile(user: IUser): Observable<IUser> {
        return this.http.put(environment.api + '/auth/user', user)
                   .map(this.handleNewTokenResponse.bind(this));
    }

    public hasRole(role: string): boolean {
        return this.user && this.user.roles && this.user.roles.includes(role);
    }

    public hasAnyRoleOf(...roles: string[]): boolean {
        if (this.user && this.user.roles) {
            for (const role of roles) {
                if (this.user.roles.includes(role)) {
                    return true;
                }
            }
        }
        return false;
    }

    private handleNewTokenResponse(resp: {token: string}): void {
        Auth.setToken(resp.token);
        this.emitTokenChange();
        this.startRenewing();
    }

    private emitTokenChange() {
        this.tokenChange.next(this.isLoggedIn());
    }

    private startRenewing(): void {
        if (!this.isLoggedIn()) {
            return;
        }
        const token = Auth.token;
        this.clearTimeout();
        const expiration = this.jwtHelper.getTokenExpirationDate(token).valueOf(),
              now        = new Date().valueOf(),
              interval   = Math.max(expiration - now - 30000, 0);
        this.timeout = setTimeout(this.refresh.bind(this), interval);
    }

    private clearTimeout(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
    }
}



// WEBPACK FOOTER //
// /Users/manuel/Development/kkk/frontend/src/app/auth/auth.service.ts